import { postRequestResetPass, postRequestResetPassConfirm, } from "@/api/user/home";
import OtpInput from "@/shared/components/common/otp";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgress, Grid, IconButton, InputAdornment, TextField, } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { Dispatch, SetStateAction, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import useMaskedCardNumber from "@/hooks/useMaskedCardNumber";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const formSchema = z.object({
  otp: z.string().min(1, "کد تایید الزامی است"),
  pass: z.string().min(6, "رمز عبور باید حداقل 6 کاراکتر باشد"),
  passRepeat: z.string(),
});
type FormData = z.infer<typeof formSchema>;
const formatTime = (seconds: number) => {
  const min = Math.floor(seconds / 60)
    .toString()
    .padStart(1, "0");
  const sec = (seconds % 60).toString().padStart(2, "0");
  return `${min}:${sec}`;
};
/**
 * ForgetPasswordOtp
 *
 * کامپوننت مربوط به مرحله تایید OTP و تغییر رمز عبور در فرآیند فراموشی رمز عبور.
 * این فرم شامل وارد کردن کد OTP ارسال شده به شماره موبایل کاربر و تعیین رمز عبور جدید است.
 *
 * ویژگی‌ها:
 * - استفاده از react-hook-form و Zod برای اعتبارسنجی فرم
 * - نمایش و مدیریت مقادیر OTP و رمز عبور
 * - قابلیت نمایش/عدم نمایش رمز عبور و تکرار آن
 * - قابلیت ارسال مجدد OTP با تابع onResend
 * - ارسال درخواست تایید OTP و تغییر رمز عبور به سرور با react-query
 * - نمایش پیام خطای سرور با toast
 * - نمایش زمان باقی مانده برای دریافت مجدد OTP
 *
 * Props:
 * @param setIsForgetPassword - تابع برای نمایش یا مخفی کردن فرم فراموشی رمز عبور
 * @param userData - اطلاعات کاربر شامل شماره موبایل و شناسه ملی
 * @param valuePhone - شماره موبایل کاربر
 * @param valueUserName - شناسه ملی کاربر
 *
 * @returns JSX.Element
 */
interface ForgetPasswordOtpProps {
  setIsForgetPassword: Dispatch<SetStateAction<boolean>>;
  userData: { phone: string; nID: string };
  valuePhone: any;
  valueUserName: string;
}
const ForgetPasswordOtp = ({ setIsForgetPassword, userData, valuePhone, valueUserName, }: ForgetPasswordOtpProps) => {
  const { handleSubmit, control, register, formState: { errors, isSubmitting }, } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { mutate: fetchRequestResetPassPhone } = useMutation({
    mutationFn: postRequestResetPass,
    onSuccess: (data: any) => { },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const onSubmitReset = async (data: FormData) => {
    const requestBody = {
      mobile: valuePhone,
      nID: valueUserName,
      patternID: 42,
    };
    fetchRequestResetPassPhone(requestBody);
  };
  const [timeLeft, setTimeLeft] = React.useState(120);

  React.useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);
  const { mutate: fetchRequestResetPass, isPending } = useMutation({
    mutationFn: postRequestResetPassConfirm,
    onSuccess: (data: any) => {
      setIsForgetPassword(false);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });
  const onSubmit = async (data: FormData) => {
    const request = {
      mobile: valuePhone,
      nID: valueUserName,
      otp: data.otp,
      pass: data.pass,
    };
    fetchRequestResetPass(request);
  };
  const maskedCar = useMaskedCardNumber;
  const handleClickShowRepeatPassword = () => {
    setShowRepeatPassword((prev) => !prev);
  };
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Grid container justifyContent="center" alignItems="center" sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Grid width={'100%'} sx={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%", maxWidth: "500px" }}>
          <StackKit spacing={2} sx={{ width: "100%" }}>
            <TypoGraphyKit variant="body1" sx={{ textAlign: "center" }}>
              کد امنیتی پیامک شده به شماره موبایل {toPersianNumber(valuePhone)}{" "}
              را وارد نمایید
            </TypoGraphyKit>

            <Controller
              name="otp"
              control={control}
              render={({ field, fieldState }) => (
                <StackKit spacing={1} sx={{display:'flex',flexDirection:'column'}}>
                  <OtpInput
                    value={field.value}
                    onChange={field.onChange}
                    onComplete={(val) => field.onChange(val)}
                    hasResend
                    onResend={onSubmitReset}
                  />
                  {fieldState.error && (
                    <TypoGraphyKit variant="body2" sx={{ color: "#ff6b6b", fontSize: "12px" }}>
                      {fieldState.error.message}
                    </TypoGraphyKit>
                  )}
                </StackKit>
              )}
            />
            <StackKit sx={{ display: 'flex', flexDirection: 'column', gap: 5,marginTop:5 }}>
              <TextField
                {...register("pass")}
                type={showPassword ? "text" : "password"}
                placeholder="رمز عبور جدید"
                error={!!errors.pass}
                helperText={errors.pass?.message}
                fullWidth
                sx={{ borderRadius: "10px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...register("passRepeat")}
                type={showRepeatPassword ? "text" : "password"}
                placeholder="تکرار رمز عبور"
                error={!!errors.passRepeat}
                helperText={errors.passRepeat?.message}
                fullWidth
                sx={{ borderRadius: "10px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowRepeatPassword} edge="end">
                        {showRepeatPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <CustomButton type="submit" fullWidth sx={{ height: "51px", background: "linear-gradient(90deg, #F4D7D3 21.62%, #78B9C1 87.47%) !important",}}>
                <TypoGraphyKit variant="body1">
                  {isPending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "ادامه"}
                </TypoGraphyKit>
              </CustomButton>
            </StackKit>
          </StackKit>
        </form>
      </Grid>
    </Grid>
  );
};

export default ForgetPasswordOtp;
